/**
 * Per-user display preferences for the opportunity board (ADV-1361).
 *
 * Mirrors the canonical `sidebar-store.ts` shape (persist + createJSONStorage)
 * per ADR-0038: shareable view state lives in the URL
 * (`use-opportunity-view-params.ts`), but the board's display CHROME — which
 * card fields show, density, whether empty columns render — is a per-user
 * preference, so it persists to localStorage here rather than in the URL.
 *
 * The in-popover Compact switch this `density` field backs is the sanctioned
 * board-scoped density control (spec §3(d)/§5). It is deliberately NOT the
 * Preferences display-density toggle that ADR-0019 Amendment A excludes — that
 * exclusion is about a global runtime density selector; this is a view-local
 * board affordance.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

type BoardDensity = "comfortable" | "compact";

export interface OpportunityDisplayProperties {
  amount: boolean;
  salesperson: boolean;
  /** Gated off until the §7 M2 territory view + S6 card slot land. */
  territory: boolean;
  /** Gated off until the §7 M1 `stage_entered_at` column + S6 card slot land. */
  timeInStage: boolean;
  address: boolean;
  created: boolean;
}

const DEFAULT_DISPLAY_PROPERTIES: OpportunityDisplayProperties = {
  amount: true,
  salesperson: true,
  territory: false,
  timeInStage: false,
  address: true,
  created: true,
};

const DEFAULTS = {
  properties: DEFAULT_DISPLAY_PROPERTIES,
  density: "comfortable" as BoardDensity,
  showEmptyColumns: true,
};

interface OpportunityDisplayState {
  properties: OpportunityDisplayProperties;
  density: BoardDensity;
  showEmptyColumns: boolean;
  toggleProperty: (key: keyof OpportunityDisplayProperties) => void;
  setDensity: (density: BoardDensity) => void;
  setShowEmptyColumns: (show: boolean) => void;
  reset: () => void;
}

export const useOpportunityDisplayStore = create<OpportunityDisplayState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      toggleProperty: (key) =>
        set((state) => ({
          properties: { ...state.properties, [key]: !state.properties[key] },
        })),
      setDensity: (density) => set({ density }),
      setShowEmptyColumns: (showEmptyColumns) => set({ showEmptyColumns }),
      // Fresh object literals so a reset never aliases the frozen DEFAULTS.
      reset: () =>
        set({
          properties: { ...DEFAULT_DISPLAY_PROPERTIES },
          density: DEFAULTS.density,
          showEmptyColumns: DEFAULTS.showEmptyColumns,
        }),
    }),
    {
      name: "adva-opportunity-display",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
